import { Button } from '@goodboydigital/astro';
import gsap from 'gsap';
import { NineSlicePlane, Sprite } from 'pixi.js';

import { Shared } from '../App';
import { NineSliceButton } from './buttons/NineSliceButton';
import LocalisedText from './LocalisedText';
import { Panel } from './Panel';
import { PurchasePanel } from './PurchasePanel';

const defaultOpts = {
    accessible: false,
    accessibleTitle: 'Name',
    titleText: 'Name',
    id: 'name',
    buttonTextOffset: 0,
    tabIndex: 0,
    buttonAsset: 'button-ninesliced-yellow',
    logoAsset: 'logo-at',
    characterSprite: new Sprite(),
    unlocked: false,
    new: true,
    unlockCoinCost: 100,
    unlockDiamondCost: 5,
    inFocus: false,
    tweenDuration: 0.2,
    currentlySelected: false,
};

export type CharacterCardOpts = typeof defaultOpts;

export class CharacterCard extends Button
{
    public view: Sprite;
    public newText: LocalisedText;
    public titleText: LocalisedText;
    public purchasePanel: PurchasePanel;
    public selectBtn: NineSliceButton;
    public id: string;
    public itemHolder: Sprite;

    protected panel: Panel;
    protected activePanel: Panel;
    protected unlockedIcon: Sprite;
    protected selectedText: LocalisedText;
    protected selectText: LocalisedText;
    protected selectedIcon: Sprite;

    protected newIcon: Sprite;

    private _inFocus: boolean;
    private _unlocked: boolean;
    private _tweenDuration: number;
    private _currentlySelected: boolean;
    private _new: boolean;
    
    private options: CharacterCardOpts;

    constructor(opts: Partial<CharacterCardOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };
        
        const view = new Sprite();

        super(view, o);
        this.view = view;
        this.id = o.id;
        this.options = o;

        this._new = false;
        this.enabled = false;
        this._tweenDuration = o.tweenDuration;
        this._currentlySelected = o.currentlySelected;

        this.panel = new Panel({
            w: 250,
            h: 400,
        });
        this.view.addChild(this.panel.view);

        this.activePanel = new Panel({
            bg: 'panel-selection',
            w: 250,
            h: 400,
        });
        this.panel.view.addChild(this.activePanel.view);

        const activeSelectedSprite = Sprite.from(Shared.assets.getTexture('equipped-tick'));

        activeSelectedSprite.anchor.set(0.5);
        activeSelectedSprite.scale.set(1.5);
        activeSelectedSprite.position.set(85, 160);

        this.activePanel.view.addChild(activeSelectedSprite);

        this.view.anchor.set(0.5);

        this.itemHolder = Sprite.from(Shared.assets.getTexture('item-holder-large'));

        this.itemHolder.anchor.set(0.5);
        this.itemHolder.scale.set(1.5);
        this.itemHolder.addChild(o.characterSprite);
        this.itemHolder.y -= 80;

        this.view.addChild(this.itemHolder);

        this.titleText = new LocalisedText(o.titleText, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 5,
            lineJoin: 'round',
            align: 'center',
            fontSize: 36,
            maxWidth: 220,
            optionalTranslation: true,
        });

        this.titleText.y += 50;
        this.titleText.anchor.set(0.5);
        this.titleText.resolution = 1.5;
        this.view.addChild(this.titleText);

        const logo = Sprite.from(Shared.assets.getTexture(o.logoAsset));

        logo.scale.set(0.95);
        logo.anchor.set(0.5);
        logo.y += 125;
        this.view.addChild(logo);

        this.unlockedIcon = Sprite.from(Shared.assets.getTexture('icon-locked'));

        this.unlockedIcon.scale.set(1.3);
        this.unlockedIcon.x = 40;
        this.unlockedIcon.y = 125;

        this.panel.view.addChild(this.unlockedIcon);

        const selectBgWidth = 255;
        const selectBgHeight = 60;

        this.selectedText = new LocalisedText('characterSelectScreen_selectedText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6 * 2,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26 * 2,
            maxWidth: 200,
            maxHeight: 30
        });
        this.selectedText.anchor.set(0.5);
        this.selectedText.y = 195;
        view.addChild(this.selectedText);

        this.selectedIcon = Sprite.from(Shared.assets.getTexture('equipped-tick'));
        this.selectedIcon.anchor.set(0.5);
        this.selectedIcon.y = this.selectedText.y -30;
        view.addChild(this.selectedIcon);

        const selectIcon = new Sprite();

        selectIcon.anchor.set(0.5);

        this.selectText = new LocalisedText('characterSelectScreen_selectText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6 * 2,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26 * 2,
            maxWidth: 200,
            maxHeight: 30
        });
        this.selectText.anchor.set(0.5);
        selectIcon.addChild(this.selectText);

        this.selectBtn = new NineSliceButton({
            icon: selectIcon,
            accessibleTitle: 'Back',
            bgAsset: 'button-ninesliced-yellow',
            hoverScale: 1.05,
            clickScale: 1.05,
            w: selectBgWidth - 10,
            h: selectBgHeight - 10,
        });
        this.selectBtn.view.y = 195;
        this.view.addChild(this.selectBtn.view);
        this.selectBtn.enabled = o.unlocked;

        this.purchasePanel = new PurchasePanel({
            enableCoinPurchase: true,
            enableDiamondPurchase: true,
            enableCancel: false,
            unlockCoinCost: o.unlockCoinCost,
            unlockDiamondCost: o.unlockDiamondCost,
        });
        this.purchasePanel.view.y = 190;

        this.view.addChild(this.purchasePanel.view);

        this._inFocus = o.inFocus;
        this._unlocked = o.unlocked;

        this.setFocus(o.inFocus);
        this.unlocked = o.unlocked;

        this.newIcon = new Sprite();

        this.newIcon.anchor.set(0.5);
        this.newIcon.position.set(90, -190);

        const bg = new NineSlicePlane(Shared.assets.getTexture('button-ninesliced-magenta-clean'),
            15, 15, 15, 15,
        );

        bg.width = 70;
        bg.height = 30;
        bg.scale.set(1.8);
        bg.x -= 35 * 1.8;
        bg.y -= 15 * 1.8;

        this.newIcon.addChild(bg);

        this.newText = new LocalisedText('characterSelectScreen_newText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 5,
            lineJoin: 'round',
            align: 'center',
            fontSize: 28,
        });

        this.newText.anchor.set(0.5);
        this.newText.y = -2;
        this.newIcon.addChild(this.newText);

        this.view.addChild(this.newIcon);

        this.new = o.new;
        this.currentlySelected = o.currentlySelected;
    }
    
    public show(): void
    {
        //i18n.translate(`character_${this.options.id}`),
        this.titleText.setText(`character_${this.options.id}`);
    }

    setFocus(focus: boolean, animate = false): void
    {
        if (this._inFocus === focus) return;
        this._inFocus = focus;

        gsap.to(this.panel.view, {
            alpha: focus ? 0 : 1,
            duration: animate ? this._tweenDuration : 0,
        });
        gsap.to(this.itemHolder, {
            alpha: focus ? 0 : 1,
            duration: animate ? this._tweenDuration : 0,
        });
        gsap.to(this.newIcon, {
            alpha: focus ? 0 : 1,
            duration: animate ? this._tweenDuration : 0,
        });

        this.unlockedIcon.visible = !this._unlocked && !focus;
        this.purchasePanel.setVisible(!this._unlocked && focus && !this._currentlySelected);
        this.selectBtn.view.visible = this._unlocked && !this._currentlySelected && focus;
        this.selectedText.visible = this._currentlySelected && focus;
        this.selectedIcon.visible = this._currentlySelected && focus;
        this.activePanel.view.visible = focus;
        
    }

    get scale(): number { return this.view.scale.x; }
    set scale(val: number)
    {
        this.view.scale.set(val);
    }

    get unlocked(): boolean { return this._inFocus; }
    set unlocked(val: boolean)
    {
        this._unlocked = val;

        this.unlockedIcon.visible = !this._inFocus && !val;
        this.purchasePanel.setVisible(this._inFocus && !val);
        this.selectBtn.enabled = true;
        this.selectBtn.view.visible = this._unlocked && !this._currentlySelected && this._inFocus;
    }

    get new(): boolean { return this._new; }
    set new(val: boolean)
    {
        this._new = val;

        this.newIcon.visible = val;
    }

    get currentlySelected(): boolean { return this._currentlySelected; }
    set currentlySelected(val: boolean)
    {
        this._currentlySelected = val;
        this.selectBtn.view.visible = !val && this._unlocked && this._inFocus;
        this.selectedText.visible = val && this._inFocus;
        this.selectedIcon.visible = val && this._inFocus;
        this.activePanel.view.visible = val;
    }
}
